import {
    Button,
    Checkbox,
    Chip,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { IdField } from '~/components/Fields';
import { DoseInfo } from '~/components/InfoTooltip/DoseInfo';
import ImageInfo from '~/components/InfoTooltip/ImageInfo';
import { StructureSetInfo } from '~/components/InfoTooltip/StructureSetInfo';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { getModalityIcon, Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import transformDoseMatrixToDoseData from '~/utils/transforms/doseMatrixTransforms';
import { SeriesData } from '~/utils/transforms/patientDataTransforms';
import { ActiveItem } from '../../useActiveItems';
import { SelectedDatasets } from '../LoadedPatientData';

type DatasetRowProps = {
    row: SeriesData;
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onCheckSelect: (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        serie: SeriesData
    ) => void;
};

export default function PatientDatasetRow({
    row,
    selectedDatasets,
    onCheckSelect,
    activeItems,
}: DatasetRowProps) {
    const [isHovered, setIsHovered] = useState(false);
    const isActive = activeItems.some((item) => item.dicomUid === row.id);
    const isSelected = selectedDatasets[row.modality]
        ? Object.keys(selectedDatasets[row.modality]).some(
              (seriesId) => seriesId === row.id
          )
        : false;

    const renderTooltip = () => {
        switch (row.modality) {
            case Modality.CT:
            case Modality.MR:
                return (
                    <Typography color="primary">
                        <ImageInfo image={row.seriesDataObject as Image3D} />
                    </Typography>
                );
            case Modality.RTSTRUCT:
                return (
                    <Typography color="primary">
                        <StructureSetInfo
                            structureSet={row.seriesDataObject as StructureSet}
                        />
                    </Typography>
                );
            case Modality.RTDOSE:
                return (
                    <Typography color="primary">
                        <DoseInfo
                            dose={transformDoseMatrixToDoseData(
                                row.seriesDataObject as DoseMatrix,
                                1
                            )}
                        />
                    </Typography>
                );
            default:
                return null;
        }
    };

    return (
        <TableRow
            key={row.id}
            hover
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={-1}
            selected={isSelected}
            sx={(theme) => ({
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
                ...(isActive && {
                    backgroundColor: 'rgba(0, 165, 185, 0.05)',
                }),
            })}
            onClick={(event) => onCheckSelect(event, row)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <TableCell
                sx={{
                    width: '10px',
                    py: 0.5,
                    px: 1,
                }}
                onClick={(event) => {
                    event.stopPropagation();
                    onCheckSelect(event, row);
                }}
            >
                <Checkbox
                    disableRipple
                    size="small"
                    color="primary"
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                    }}
                    sx={{
                        '&': {
                            padding: 0,
                        },
                    }}
                />
            </TableCell>

            <TableCell
                sx={{
                    py: 0.5,
                    px: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <Typography
                    display="flex"
                    alignItems="center"
                    gap={1}
                    color="primary"
                    fontSize={14}
                >
                    {getModalityIcon(row.modality)} {row.modality}
                </Typography>
                {renderTooltip()}
            </TableCell>
            <TableCell
                sx={{
                    py: 0.5,
                    px: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: 14,
                }}
            >
                {row.description}
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, opacity: 0.7 }}>
                {row.seriesDate?.toDateString()}
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, opacity: 0.7 }}>
                <SensitiveInfoTypography sx={{ fontSize: 12 }}>
                    <IdField id={row.id} />
                </SensitiveInfoTypography>
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                {isActive ? (
                    <Chip
                        variant="outlined"
                        size="small"
                        sx={{
                            fontSize: 10,
                            height: 20,
                            padding: 0.5,
                            lineHeight: 0.9,
                        }}
                        color="success"
                        label={<FormattedMessage id="active" />}
                    />
                ) : (
                    isHovered && (
                        <Button
                            variant="text"
                            color="primary"
                            size="small"
                            sx={{
                                py: 0,
                                fontSize: 12,
                                minWidth: 'auto',
                            }}
                        >
                            <FormattedMessage id="select" />
                        </Button>
                    )
                )}
            </TableCell>
        </TableRow>
    );
}
